import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'new-star-rating-conviva',
  template: `
  <div class="star-rating">
    @for( star of stars;track i;let i = $index){

      <i
         [class.fa-regular]="!star || rating === 0"
         [class.fa-solid]="star && rating !== 0"
         class="fa-star"
         [class.red-star]="rating === 1 || rating === 2"
         [class.yellow-star]="rating === 3"
         [class.blue-star]="rating === 4 || rating === 5"
         [class.disabled]="isDisabled"
         [class.read-only]="isReadOnly"
         [class.hover-on]="!isReadOnly"
         (click)="onStarClick(i)"
         (blur)="onTouched()">
      </i>
    }
</div>
`,
  styleUrls: ['../star-rating.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NewStarRatingConvivaComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS, //  ← registra como validador
      useExisting: forwardRef(() => NewStarRatingConvivaComponent),
      multi: true
    }
  ],
  standalone: true,
  imports:[CommonModule]
})
export class NewStarRatingConvivaComponent implements ControlValueAccessor, Validator {

  @Input() starCount = 5;
  @Input() checkboxNumber?: number;
  @Input() isReadOnly = false;

  /* ---------- Outputs ---------- */
  @Output() ratingChange = new EventEmitter<number>();

  /* ---------- Estado interno ---------- */
  private _rating = 0;
  get rating() { return this._rating; }

  stars: boolean[] = Array(this.starCount).fill(false);

  /* ---------- Angular helpers ---------- */
  private onChange: (v: number) => void = () => { };
  public onTouched: () => void = () => { };
  private onValidatorChange = () => { };
  isDisabled = false;

  constructor(
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef
  ) { }

  /* ===== ControlValueAccessor ===== */
  writeValue(v: number | null): void {
    if (v !== null && v !== undefined) {
      this.setRating(v, /*emit*/ false);
      this.onValidatorChange();           // <- mantém validade atualizada
    }
  }
  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }
  setDisabledState(is: boolean): void {
    this.isDisabled = is;
    this.cdr.markForCheck();
  }

  /* ===== Validator ===== */
  validate(_: AbstractControl): ValidationErrors | null {
    return this._rating > 0 ? null : { ratingRequired: true };
  }
  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  /* ---------- Interação do usuário ---------- */
  onStarClick(index: number): void {
    if (this.isDisabled || this.isReadOnly) { return; }
    const newRating = index + 1;
    this.applyBusinessRules(newRating);
    this.setRating(this._rating);         // regras podem alterar o valor
  }

  /* ---------- Métodos auxiliares ---------- */
  private setRating(value: number, emit = true): void {
    this._rating = value ?? 0;
    this.stars = this.stars.map((_, i) => i < this._rating);

    if (emit) {
      this.onChange(this._rating);
      this.ratingChange.emit(this._rating);
      this.onValidatorChange();           // <-- avisa o Angular Forms
    }
    this.cdr.markForCheck();              // OnPush
  }

  private applyBusinessRules(rating: number): void {
    if (!this.checkboxNumber) {           // sem regra especial
      this._rating = rating;
      return;
    }
    const cb = this.checkboxNumber;
    if (
      (rating !== 3 && cb === 2) ||
      (rating < 4 && cb === 1) ||
      (rating > 2 && cb === 3)
    ) {
      this.toastr.error('Quantidade de estrelas incompatível com a resposta selecionada.');
      this._rating = cb === 2 ? 3 : cb === 1 ? 4 : 2;
    } else {
      this._rating = rating;
    }
  }
}
